import { useQuery } from '@tanstack/react-query';
import { getMyResellerApi, type ApiReseller } from '@/api/vendo';
import useAuthStore from '@/store/authStore';

/**
 * Source of truth for whether the current user is an approved SmartFi Reseller.
 * Derived from `getMyResellerApi` (the caller's own reseller record under their
 * distributor):
 *
 *   - approved reseller: record exists with status 'approved'.
 *   - pending: record exists with status 'pending'.
 *
 * Mirrors `useDistributorStatus`. Skipped when no user is logged in so the query
 * doesn't fire on public routes.
 */
export interface ResellerStatus {
   isReseller: boolean;
   isPending: boolean;
   reseller: ApiReseller | null;
   isLoading: boolean;
}

export function useResellerStatus(): ResellerStatus {
   const isAuthed = useAuthStore((s) => s.isAuthenticated);
   const { data, isLoading } = useQuery({
      queryKey: ['vendo', 'reseller', 'me'],
      queryFn: getMyResellerApi,
      enabled: isAuthed,
   });
   const reseller = data?.reseller ?? null;
   return {
      isReseller: reseller?.status === 'approved',
      isPending: reseller?.status === 'pending',
      reseller,
      isLoading,
   };
}
